/* eslint-disable no-unsafe-finally */

import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { DateRange, TimestampRange, UTC } from 'rewire-common';
import { GQL } from 'rewire-graphql';
import {
  AreaFilter,
  AreaSort,
  AuditLogFilterInput,
  AuditLogSortInput,
  BatchFilterInput,
  DeepPartial,
  EmployeeFilter,
  EmployeeSort,
  EntityType,
  FieldFilter,
  GeneratorFilter,
  GeneratorSort,
  GroupFilter,
  GroupSort,
  IApiKey,
  IArea,
  IAuditLog,
  IAuthenticationOptions,
  IBatch,
  IBatchEmployeeTimecardResult,
  IBatchTimecard,
  IConfirmationStatus,
  IDashboardGuestToken,
  IDocument,
  IEmployee,
  IEmployeeSummary,
  IEmployeeTimecardStatus,
  IFilter,
  IGenerated,
  IGenerator,
  IGroup,
  IJob,
  IJobSearchResult,
  ILocation,
  IMessage,
  IMessageLevelCount,
  INameEntity,
  INameLike,
  IOccupation,
  IOrganization,
  IReason,
  IReport,
  IReportFormatInput,
  IReportInput,
  IResponse,
  IResult,
  IRole,
  IRuleSetTable,
  ISavedSchedule,
  ISchedule,
  IScheduleRuleContext,
  IScheduleSearchResult,
  ISchedulingToolSchedule,
  ISearchResult,
  ISecurityToken,
  ISimplifiedBatch,
  ISort,
  ITemplate,
  ITimecard,
  ITimecardStatusFilter,
  ITimecardSummary,
  ITimecardTest,
  ITimeclock,
  ITimeOffAccrual,
  ITimeOffRequest,
  ITimeOffRequestStatus,
  ITimeOffType,
  ITimeOffTypeTransaction,
  ITypeInfo,
  IUdf,
  IUdfDocumentTypes,
  IUdt,
  IUser,
  IValidationRuleSet,
  IView,
  IWorkflow,
  JobInfoInput,
  LocationFilter,
  LocationSort,
  MessageFilterInput,
  OccupationFilter,
  OccupationSort,
  OrganizationFilter,
  OrganizationSort,
  ReasonFilter,
  ReasonSort,
  ReportFilterInput,
  ReportSecondaryFilterInput,
  ResponseFilter,
  ScheduleFilterInput,
  ScheduleSort,
  TemplateFilter,
  TemplateSort,
  TimecardFilterInput,
  TimecardSort,
  TimecardTestFilterInput,
  TimeclocksFilter,
  TimeclocksSort,
  TimeOffRequestFilterInput,
  TimeOffRequestSort,
  TimeOffType,
  TimeOffTypeFilter,
  TimeOffTypeSort,
  UdfDocumentType,
  UdfFilter,
  UdfSort,
  UserFilter,
  UserSort,
  ViewFilter,
  ViewSort,
} from '../DataTypes';
import { guardedQuery, query, subscribe } from './GraphQLClient';

const defaultReturnSchema = 'id';

const getConfirmationStatusQuery: DocumentNode = gql`
  query($token: String!) {
    getConfirmationStatus(token: $token) {
      organization
      location
      email
      name
      phone
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// email
// avatar
// phone
// employee
// tags
// fields
// organization
// location
// userRights
const getSessionInfoQuery = (returnSchema: string): DocumentNode => gql`
  query {
    getSessionInfo {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getDashboardGuestTokenQuery = gql`
  query($id: ID!) {
    getDashboardGuestToken(id: $id)  {
      id
      token
    }
  }
`;

const getDashboardsQuery = gql`
  query {
    getDashboards {
      id
      name
    }
  }
`;

const getSecurityTokens = (returnSchema: string): DocumentNode => gql`
  query {
    securityTokens {
      ${returnSchema}
    }
  }
`;

const hasEmployeeMobileAccessQuery: DocumentNode = gql`
  query($email: String!) {
    hasEmployeeMobileAccess(email: $email)
  }
`;

// *** Return Schema ***
// JSON
const getRuleSetQuery: DocumentNode = gql`
  query($ruleSetId: String!) {
    getRuleSet(ruleSetId: $ruleSetId) {
      id
      name
    }
  }
`;

const getRuleTable: DocumentNode = gql`
  query($tableId: String!) {
    getRuleTable(tableId: $tableId) {
      schemaDefn {
        id
        name
        schema
      }
      rows
    }
  }
`;

// *** Return Schema ***
// data: [User!]!
// id
// code
// name
// email
// avatar
// phone
// employee
// tags
// fields
// organization
// location
// userRights
const getUsersQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: UserFilter, $sort: UserSort, $first: Int, $after: Cursor) {
    users(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// email
// avatar
// phone
// employee
// tags
// fields
// organization
// location
// userRights
const getUsersByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    usersByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// id
// name
// tz
// country
// state
// right
const getApiKeysQuery = (returnSchema: string): DocumentNode => gql`
  query {
    getApiKeys {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Organization!]!
// id
// code
// name
// tags
// fields
// locations
// users
const getOrganizationsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    organizations(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data: {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const responsesSearchQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: ResponseFilterInput, $first: Int, $after: Cursor) {
    responsesSearch(filter: $filter, first: $first, after: $after) {
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// locations
// users
const getOrganizationsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    organizationsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Location!]!
// id
// code
// name
// tags
// fields
// organization
// udfs
// areas
// users
// tz
// weekStart
// timeFormat
// postedDate
// workflow
// country
// state
// timeOffRequestWorkflow
const getLocationsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: LocationFilter, $sort: LocationSort, $first: Int, $after: Cursor) {
    locations(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// organization
// udfs
// areas
// users
const getLocationsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    locationsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Area!]!
// id
// code
// name
// tags
// fields
// location
// organization
// groups
const getAreasQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: AreaFilter, $sort: AreaSort, $first: Int, $after: Cursor) {
    areas(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// location
// organization
// groups
const getAreasByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    areasByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Group!]!
// id
// code
// name
// tags
// fields
// position
// area
// scheduleOptions
const getGroupsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: GroupFilter, $sort: GroupSort, $first: Int, $after: Cursor) {
    groups(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// position
// area
// scheduleOptions
const getGroupsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    groupsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Employee!]!
// id
// code
// name
// tags
// fields
// email
// phone
// timecards
const getEmployeesQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EmployeeFilter, $sort: EmployeeSort, $first: Int, $after: Cursor) {
    employees(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getRolesQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: RoleFilter, $sort: RoleSort, $first: Int, $after: Cursor) {
    roles(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// email
// phone
// timecards
const getEmployeesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    employeesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getRolesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    rolesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// const getJobsQuery = gql`
//   query($ids: [ID!]!) {
//     jobs(ids: $ids) {
//       id
//       group
//       generator
//       occupation
//       employee
//       cycleOffset
//       position
//       effective
//     }
//   }
// `;

// *** Return Schema ***
// id
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const getJobsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    jobsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// job
// template
// earnedDate
// version
const getGeneratedByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [GeneratedID!]!) {
    generatedByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Generator!]!
// id
// code
// name
// tags
// fields
// cycle
// locked
// displayStartDate
const getGeneratorsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    generators(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// cycle
// locked
// displayStartDate
const getGeneratorsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    generatorsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// cycle
// locked
// displayStartDate
const getDocumentByIdsQuery: DocumentNode = gql`
  query($ids: [ID!]!) {
    documentsByIds(ids: $ids) {
      id
      code
      name
      tags
      fields
    }
  }
`;

// *** Return Schema ***
// data: [Occupation!]!
// id
// code
// name
// tags
// fields
const getOccupationsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    occupations(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// data: [Reports!]!
// id
// code
// name
// tags
// fields
const getReportsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    reports(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// data: [Reports!]!
// id
// code
// name
// tags
// fields
const runReportQuery: DocumentNode = gql`
  query($report: ReportInput!, $effective: DateRange, $filter: ReportFilterInput, $groupBy: [String!], $sort: ReportSort, $viewId: String, $options: JSON, $formats: ReportFormatInput $secondaryFilter: ReportSecondaryFilterInput) {
    runReport(report: $report, effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, viewId: $viewId, options: $options, formats: $formats, secondaryFilter: $secondaryFilter)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
const getOccupationsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    occupationsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// text
// level
// type
// effective
// employee
// job
// group
// area
// batch
// createdDate
// createdBy
const getMessagesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    messagesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getAuthenticationOptionsQuery = gql`
  query {
    getAuthenticationOptions {
      saml
      usernamePassword
      apple
      mfa
      provider {
        metadataURL
        entityID
        replyURL
      }
    }
  }
`;

// *** Return Schema ***
// data: [Reason!]!
// id
// code
// name
// tags
// fields
// category
const getReasonsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: ReasonFilter, $sort: ReasonSort, $first: Int, $after: Cursor) {
    reasons(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// data: [Reason!]!
// id
// code
// name
// tags
// fields
// category
const getDocumentsQuery = (returnSchema: string): DocumentNode => gql`
  query($type: String!, $filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    documents(type: $type, filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;
// *** Return Schema ***
// data: [Reason!]!
// id
// code
// name
// tags
// fields
// category
const getTimeOffTypesQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    timeOffTypes(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getEmployeeTimeOffTypesQuery: DocumentNode = gql`
  query timeOffTypes($employeeId: String, $asof: Date!) {
    timeOffTypes {
      data {
        id
        name
        trackingUnits
        trackingAmounts
        withdrawalReasons {
          id
          name
        }
        accrualReasons {
          id
          name
        }
        messages(employeeIds: [$employeeId], asof: $asof, type: accrual) {
          id
          type
          level
          text
        }
        totals(employeeIds: [$employeeId], asof: $asof) {
          byUnits {
            totalAccruals
            totalWithdrawals
            balance
            plannedWithdrawals
            usedWithdrawals
          }
          byAmount {
            totalAccruals
            totalWithdrawals
            balance
            plannedWithdrawals
            usedWithdrawals
          }
        }
        transactions(employeeIds: [$employeeId], asof: $asof) {
          transactions {
            id
            type
            units
            amount
            effective
            source
            earnedDate
            messages {
              id
              type
              level
              text
            }
          }
        }
      }
    }
  }
`;

const getTimeOffTypeTransactionsQuery: DocumentNode = gql`
  query timeOffTypesByIds($timeOffTypeId: ID!, $employeeId: String, $asof: Date!) {
    timeOffTypesByIds(ids: [$timeOffTypeId]) {
      messages(employeeIds: [$employeeId], asof: $asof, type: accrual) {
        id
        type
        level
        text
      }
      transactions(employeeIds: [$employeeId], asof: $asof) {
        transactions {
          id
          type
          units
          amount
          effective
          source
          earnedDate
          messages {
            id
            type
            level
            text
          }
        }
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// category
const getReasonsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    reasonsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// trackingUnits
// trackingAmounts
// withdrawalReasons
const getTimeOffTypesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    timeOffTypesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [Template!]!
// id
// code
// name
// tags
// fields
// version
// template
// locked
const getTemplatesQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: EntityFilter, $sort: EntitySort, $first: Int, $after: Cursor) {
    templates(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// version
// template
// locked
const getTemplatesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    templatesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// data: [View!]!
// id
// code
// name
// tags
// fields
// type
// options
const getViewsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: ViewFilter, $sort: ViewSort, $first: Int, $after: Cursor) {
    views(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// type
// options
const getViewsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    viewsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// code
// earnedDate
// effective
// accountCode
// units
// factor
// rate
// amount
// state
// createdBy
// createdDate
// tags
// job
// employee
// reason
// overtimeReason
// generated
// group
// fields
// occupation
const getTimecardsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    timecardsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// code
// earnedDate
// effective
// accountCode
// units
// factor
// rate
// amount
// state
// createdBy
// createdDate
// tags
// job
// employee
// reason
// overtimeReason
// generated
// group
// fields
// occupation
const getTimecardSearchQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange, $filter: TimecardFilterInput, $sort: TimecardSort, $first: Int, $after: Cursor) {
    timecardSearch(effective: $effective, filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// employee
// effective
// area
// group
// occupation
// generator
// total
// difference
// status
// lastRan
// lastEditedBy
// originalTimecards
// actualTimecards
// testDetails (ITimecardTestDetail[])
const getTimecardTestsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: TimecardTestFilterInput, $sort: TimecardTestSort, $first: Int, $after: Cursor) {
    timecardTests(filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getTimecardTestsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    timecardTestsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// add the following when it's available: areaId
const getBatchesSummaryQuery: DocumentNode = gql`
  query($filter: BatchFilterInput, $first: Int, $after: Cursor) {
    batchesSummary(filter: $filter, first: $first, after: $after) {
      totalCount
      cursor
      data {
        id
        name
        code
        status
        effective
        createdDate
        createdBy
        timecardCount
        gross
        exportStatus
      }
    }
  }
`;

const getTypeInfoQuery: DocumentNode = gql`
  query($type: String!) {
    __type(name: $type) {
      fields {
        name
        args {
          name
          defaultValue
          description
          type {
            kind
            name
          }
        }
        type {
          description
          ofType {
            kind
            name
          }
          kind
          name
        }
      }
    }
  }
`;

// *** Return Schema ***
// data: [Udf!]!
// id
// documentType
// type
// name
// isSystem
// isList
// displayName
// allowableValues
// location
const getUdfsQuery = (returnSchema: string): DocumentNode => gql`
  query($documentType: String, $filter: UdfFilter, $sort: UdfSort, $first: Int, $after: Cursor) {
    udfs(documentType: $documentType, filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getTagsQuery: DocumentNode = gql`
  query($documentType: String!, $search: String) {
    tags(documentType: $documentType, search: $search)
  }
`;

// *** Return Schema ***
// id
// documentType
// type
// name
// isSystem
// isList
// displayName
// allowableValues
// location
const getUdfsByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    udfsByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getBuiltInUdfsQuery = (returnSchema: string): DocumentNode => gql`
  query($documentType: String) {
    builtInUdfs(documentType: $documentType) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getUdfDocumentTypesQuery: DocumentNode = gql`
  query {
    udfDocumentTypes
  }
`;

const defaultValuesSearchReturnSchema = 'id name code';
const getUdfsValuesSearchQuery = (returnSchema?: string): DocumentNode => gql`
  query($ids: [ID!]!, $search: String) {
    udfsByIds(ids: $ids) {
      valuesSearch(search: $search) {
        data {
          ${returnSchema || defaultValuesSearchReturnSchema}
          id
          name
          code
        }
      }
    }
  }
`;

// *** Return Schema ***
// id
// type
// code
// name
// display
// other
const getSearchQuery = (returnSchema: string): DocumentNode => gql`
  query($type: EntityType!, $search: String, $filter: FieldFilter, $count: Int) {
    search(type: $type, search: $search, filter: $filter, count: $count) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getApplicationTemplatesSearchQuery = gql`
  query($search: String) {
    getApplicationTemplatesSearch(search: $search) {
      id
      name
    }
  }
`;

// *** Return Schema ***
// id
// effective
// eventType
// location
// comment
// lastEditedBy
// lastEditedDate
// employee
const getTimeclocksQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: TimestampRange, $filter: TimeclockFilter, $sort: TimeclockSort, $first: Int, $after: Cursor) {
    timeclocks(effective: $effective, filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// employee
// schedule (shift[])
const getSchedulingToolEmployeeScheduleQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $ruleContext: ScheduleRuleContext!, $ruleQuery: String!) {
    schedulingToolEmployeeSchedule(effective: $effective, ruleContext: $ruleContext, ruleQuery: $ruleQuery) {
      took
      totalCount
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
// area
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const getEmployeeScheduleQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $filter: ScheduleFilterInput, $groupBy: [String!], $sort: ScheduleSort, $first: Int, $after: Cursor) {
    employeeSchedule(effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
// area
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const getJobScheduleQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $filter: ScheduleFilterInput, $groupBy: [String!], $sort: ScheduleSort, $first: Int, $after: Cursor) {
    jobSchedule(effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const scheduleSearchQuery = (returnSchema: string): DocumentNode => gql`
  query($search: String!, $effective: DateRange, $count: Int, $from: Int) {
    scheduleSearch(search: $search, effective: $effective, count: $count, from: $from) {
      ${returnSchema}
    }
  }
`;

const jobSearchQuery = (returnSchema: string): DocumentNode => gql`
  query($search: String!, $effective: Date, $groupId: ID, $employeeId: ID, $count: Int, $from: Int) {
    jobSearch(search: $search, groupId: $groupId, employeeId: $employeeId, effective: $effective, count: $count, from: $from) {
      ${returnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
const getGetJobScheduleQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $jobIds: [ID!]!) {
    getJobSchedule(effective: $effective, jobIds: $jobIds) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
const getGetEmployeeScheduleQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $employeeIds: [ID!]!) {
    getEmployeeSchedule(effective: $effective, employeeIds: $employeeIds) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
// area
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const getRegenerateJobScheduleQuery = gql`
  query($jobInfo: [JobInfoInput]) {
    regenerateJobSchedule(jobInfo: $jobInfo)
  }
`;

const getBatchesQuery: DocumentNode = gql`
  query($filter: BatchFilter, $first: Int, $after: Cursor) {
    batches(filter: $filter, first: $first, after: $after) {
      data {
        id
        code
        name
        status
      }
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// email
// phone
// timecards
const getBatchesByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    batchesByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getTimecardWorkflowsQuery: DocumentNode = gql`
  query {
    workflows {
      id
      code
      name
    }
  }
`;

const getLocationWorkflowQuery: DocumentNode = gql`
  query {
    locations {
      data {
        workflow {
          id
          name
          finalState
        }
      }
    }
  }
`;

const getPossibleTimecardStatusesQuery: DocumentNode = gql`
  query { possibleTimecardStatuses }
`;

const getPossibleTimecardTransitionsQuery: DocumentNode = gql`
  query($state: String!) {
    possibleTimecardTransitions(state: $state)
  }
`;
const getTimecardStatusesQuery: DocumentNode = gql`
  query($employeeIds: [String!]!, $effective: DateRange!, $filter: TimecardStatusFilterInput) {
    getTimecardStatuses(employeeIds: $employeeIds, effective: $effective, filter: $filter) {
      employeeId
      isPayable
      earnedDate
      status
      lastEditedDate
      lastEditedBy
      locked
      manual
      batch {
        id
        name
        status
      }
      allowableTransitions {
        event
        enabled
      }
    }
  }
`;

// add the following when it's available: areaId
const getEmployeeTimecardSummaryQuery: DocumentNode = gql`
  query($effective: DateRange!, $filter: FieldFilterInput, $groupBy: String, $sort: EmployeeListSort, $first: Int, $after: Cursor) {
    employeeTimecardSummary(effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, first: $first, after: $after) {
      totalCount
      cursor
      columns
      # grand totals
      gross
      otherPay
      errors
      reasons {
        name
        value
      }
      groups {
        id
        name
        errors
        gross
        otherPay
        reasons {
          name
          value
        }
      }
      results {
        id
        employee: name
        status
        errors
        gross
        otherPay
        timecardType
        groupByKeys
        reasons {
          name
          value
        }
      }
    }
  }
`;

const runTimeOffAccrualRulesQuery: DocumentNode = gql`
  query {
    runTimeOffAccrualRules
  }
`;

const hasAutoScheduleRulesQuery: DocumentNode = gql`
  query {
    hasAutoScheduleRules
  }
`;

const getEmployeeSummariesQuery: DocumentNode = gql`
  query($effective: DateRange!, $filter: FieldFilterInput, $sort: EmployeeListSort, $first: Int, $after: Cursor) {
    employeeList(effective: $effective, filter: $filter, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        id
        name
        code
        warning
        error
      }
    }
  }
`;

// *** Return Schema ***
// id
// text
// level
// type
// effective
// employee
// job
// group
// area
// batch
// createdDate
// createdBy
const getMessagesQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: MessageFilterInput, $first: Int, $after: Cursor) {
    messagesSearch(filter: $filter, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getUserDefinedTypesQuery = (returnSchema: string): DocumentNode => gql`
  query {
    udts {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getMessagesCountsQuery: DocumentNode = gql`
  query($filter: MessageFilterInput) {
    messagesCounts(filter: $filter) {
      level
      count
    }
  }
`;

const rulesProcessedSubscription: DocumentNode = gql`
subscription($employeeId: [ID!]!, $effective: DateRange!) {
  processedRules(employeeIds: $employeeId, effective: $effective) {
    employeeId
    effective
    changed
  }
}`;

const getTimeOffAccrualsByIdsQuery: DocumentNode = gql`
  query($ids: [ID!]!) {
    timeOffAccrualsByIds(ids: $ids) {
      id
      effective
      units
      amount
      employee {
        id
        name
      }
      timeOffType {
        id
        name
        trackingUnits
        trackingAmounts
      }
    }
  }
`;

const getTimecardSummaryDashboardQuery: DocumentNode = gql`
  query($effective: DateRange!) {
    timecardSummaryDashboard(effective: $effective) {
      notInBatch
      inBatch
      total
      totalEmployees
      errors
      statuses {
        status
        total
      }
    }
  }
`;

const getAdjustmentsTimecardSummaryDashboardQuery: DocumentNode = gql`
  query($effective: DateRange!) {
    adjustmentsTimecardSummaryDashboard(effective: $effective) {
      notInBatch
      inBatch
      total
      totalEmployees
      errors
      statuses {
        status
        total
      }
    }
  }
`;

const getTimecardsNotInBatchQuery: DocumentNode = gql`
  query($effective: DateRange!, $areaIds: [ID!], $groupIds: [ID!], $startOfWeeks: [String!]) {
    timecardsNotInBatch(effective: $effective, areaIds: $areaIds, groupIds: $groupIds, startOfWeeks: $startOfWeeks)
  }
`;

const getTimecardsNotInAdjustmentsBatchQuery: DocumentNode = gql`
  query($effective: DateRange!, $areaIds: [ID!], $groupIds: [ID!], $startOfWeeks: [String!]) {
    timecardsNotInAdjustmentsBatch(effective: $effective, areaIds: $areaIds, groupIds: $groupIds, startOfWeeks: $startOfWeeks)
  }
`;

const loadValidationRulesQuery: DocumentNode = gql`
  query {
    loadValidationRules {
      schema {
        schemaId
        priority
        category
        parameters
        name
      }
      rules {
        id
        schemaId
        priority
        category
        parameters
        name
      }
    }
  }
`;

const getTimeOffRequestsQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $filter: TimeOffRequestFilterInput, $groupBy: [String!], $sort: TimeOffRequestSort, $first: Int, $after: Cursor) {
    timeOffRequests(effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getTimeOffRequestByIdsQuery = (returnSchema: string): DocumentNode => gql`
  query($ids: [ID!]!) {
    timeOffRequestByIds(ids: $ids) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const getTimeOffRequestsForTimeOffBalancesQuery = (returnSchema: string): DocumentNode => gql`
  query($effective: DateRange!, $filter: TimeOffRequestFilterInput, $groupBy: [String!], $sort: TimeOffRequestSort, $first: Int, $after: Cursor) {
    timeOffRequestsForTimeOffBalances(effective: $effective, filter: $filter, groupBy: $groupBy, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getTimeOffRequestStatusQuery: DocumentNode = gql`
  query($timeOffRequestId: ID!) {
    getTimeOffRequestStatus(timeOffRequestId: $timeOffRequestId) {
      id
      status
      allowableTransitions {
        event
        enabled
      }
    }
  }
`;

const getPossibleTimeOffRequestStatusesQuery: DocumentNode = gql`
  query {
    possibleTimeOffRequestStatuses
  }
`;

const getTimeOffRequestsWorkflowsQuery: DocumentNode = gql`
  query {
    timeOffRequestWorkflows {
      id
      code
      name
    }
  }
`;

// *** Return Schema ***
// id
// location
// effective
// type
// operation
// description
// entityId
// transactionId
// delta
// createdBy
// createdDate
const getAuditLogsQuery = (returnSchema: string): DocumentNode => gql`
  query($filter: AuditLogFilterInput, $entityDataFilters: [FieldFilter], $sort: AuditLogSortInput, $first: Int, $after: Cursor) {
    logs(filter: $filter, entityDataFilters: $entityDataFilters, sort: $sort, first: $first, after: $after) {
      took
      totalCount
      cursor
      data {
        ${returnSchema || defaultReturnSchema}
      }
    }
  }
`;

const getScheduleRuleDefaultsQuery: DocumentNode = gql`
  query($context: ScheduleRuleContext!) {
    getScheduleRuleDefaults(context: $context) {
      id
      name
    }
  }
`;

export default class DataQueries {
  static async getConfirmationStatus(token: string): Promise<DeepPartial<IConfirmationStatus> | undefined> {
    const result = await query(getConfirmationStatusQuery, { token });
    return result?.data?.getConfirmationStatus;
  }

  static async getUsers(
    returnSchema: string,
    filter?: UserFilter,
    sort?: UserSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IUser>>> {
    const result = await guardedQuery(getUsersQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.users;
  }

  static async getUserLocations(): Promise<IResult<DeepPartial<INameEntity>>> {
    const getUserLocationsQuery = gql`
    query {
      getUserLocations {
        id
        name
      }
    }
  `;
    const result = await guardedQuery(getUserLocationsQuery);
    return result?.data?.getUserLocations;
  }

  static async getUsersByIds(returnSchema: string, userIds: string[]): Promise<DeepPartial<IUser | undefined>[] | undefined> {
    const result = await guardedQuery(getUsersByIdsQuery(returnSchema), { ids: userIds });
    return result?.data?.usersByIds;
  }

  static async getApiKeys(returnSchema: string): Promise<DeepPartial<IApiKey | undefined>[]> {
    const result = await guardedQuery(getApiKeysQuery(returnSchema));
    return result?.data?.getApiKeys;
  }

  static async getOrganizations(
    returnSchema: string,
    filter?: OrganizationFilter,
    sort?: OrganizationSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IOrganization>>> {
    const result = await guardedQuery(getOrganizationsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.organizations;
  }

  static async responsesSearch(
    returnSchema: string,
    filter?: ResponseFilter,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IResponse>>> {
    const result = await guardedQuery(responsesSearchQuery(returnSchema), { filter, first, after });
    return result?.data?.responsesSearch?.data;
  }

  static async getOrganizationsByIds(
    returnSchema: string,
    organizationIds: string[],
  ): Promise<DeepPartial<IOrganization | undefined>[] | undefined> {
    const result = await guardedQuery(getOrganizationsByIdsQuery(returnSchema), { ids: organizationIds });
    return result?.data?.organizationsByIds;
  }

  static async getLocations(
    returnSchema: string,
    filter?: LocationFilter,
    sort?: LocationSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ILocation>>> {
    const result = await guardedQuery(getLocationsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.locations;
  }

  static async getLocationsByIds(
    returnSchema: string,
    locationIds: string[],
  ): Promise<DeepPartial<ILocation | undefined>[] | undefined> {
    const result = await guardedQuery(getLocationsByIdsQuery(returnSchema), { ids: locationIds });
    return result?.data?.locationsByIds;
  }

  static async getAreas(
    returnSchema: string,
    filter?: AreaFilter,
    sort?: AreaSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IArea>>> {
    const result = await guardedQuery(getAreasQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.areas;
  }

  static async getAreasByIds(returnSchema: string, areaIds: string[]): Promise<DeepPartial<IArea | undefined>[] | undefined> {
    const result = await guardedQuery(getAreasByIdsQuery(returnSchema), { ids: areaIds });
    return result?.data?.areasByIds;
  }

  static async getGroups(
    returnSchema: string,
    filter?: GroupFilter,
    sort?: GroupSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IGroup>>> {
    const result = await guardedQuery(getGroupsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.groups;
  }

  static async getGroupsByIds(returnSchema: string, groupIds: string[]): Promise<DeepPartial<IGroup | undefined>[] | undefined> {
    const result = await guardedQuery(getGroupsByIdsQuery(returnSchema), { ids: groupIds });
    return result?.data?.groupsByIds;
  }

  static async getEmployees(
    returnSchema: string,
    filter?: EmployeeFilter,
    sort?: EmployeeSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IEmployee>>> {
    const result = await guardedQuery(getEmployeesQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.employees;
  }

  static async getEmployeesByIds(
    returnSchema: string,
    employeeIds: string[],
  ): Promise<DeepPartial<IEmployee | undefined>[] | undefined> {
    const result = await guardedQuery(getEmployeesByIdsQuery(returnSchema), { ids: employeeIds });
    return result?.data?.employeesByIds;
  }

  static async getRoles(
    returnSchema: string,
    filter?: IFilter,
    sort?: ISort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IRole>>> {
    const result = await guardedQuery(getRolesQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.roles;
  }

  static async getRolesByIds(returnSchema: string, roleIds: string[]): Promise<DeepPartial<IRole | undefined>[] | undefined> {
    const result = await guardedQuery(getRolesByIdsQuery(returnSchema), { ids: roleIds });
    return result?.data?.rolesByIds;
  }

  static async getJobsByIds(returnSchema: string, jobIds: string[]): Promise<DeepPartial<IJob | undefined>[]> {
    const result = await guardedQuery(getJobsByIdsQuery(returnSchema), { ids: jobIds });
    return result?.data?.jobsByIds;
  }

  static async getGeneratedByIds(
    returnSchema: string,
    generatedIds: string[],
  ): Promise<DeepPartial<IGenerated | undefined>[] | undefined> {
    const result = await guardedQuery(getGeneratedByIdsQuery(returnSchema), { ids: generatedIds });
    return result?.data?.generatedByIds;
  }

  static async getGenerators(
    returnSchema: string,
    filter?: GeneratorFilter,
    sort?: GeneratorSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IGenerator>>> {
    const result = await guardedQuery(getGeneratorsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.generators;
  }

  static async getGeneratorsByIds(
    returnSchema: string,
    generatorIds: string[],
  ): Promise<DeepPartial<IGenerator | undefined>[] | undefined> {
    const result = await guardedQuery(getGeneratorsByIdsQuery(returnSchema), { ids: generatorIds });
    return result?.data?.generatorsByIds;
  }

  static async getDocumentByIds(_returnSchema: string, ids: string[]): Promise<DeepPartial<IDocument | undefined>[] | undefined> {
    const result = await guardedQuery(getDocumentByIdsQuery, { ids });
    return result?.data?.documentsByIds;
  }

  static async getOccupations(
    returnSchema: string,
    filter?: OccupationFilter,
    sort?: OccupationSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IOccupation>>> {
    const result = await guardedQuery(getOccupationsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.occupations;
  }

  static async getReports(
    returnSchema: string,
    filter?: IFilter,
    sort?: ISort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IReport>>> {
    const result = await guardedQuery(getReportsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.reports;
  }

  static async runTimeOffAccrualRules(): Promise<boolean | undefined> {
    const result = await guardedQuery(runTimeOffAccrualRulesQuery);
    return result?.data?.runTimeOffAccrualRules;
  }

  static async hasAutoScheduleRules(): Promise<boolean> {
    const result = await guardedQuery(hasAutoScheduleRulesQuery);
    return result?.data?.hasAutoScheduleRules ?? false;
  }

  static async runReport(
    report: IReportInput,
    effective?: DateRange,
    filter?: ReportFilterInput,
    groupBy?: string[],
    sort?: ScheduleSort,
    viewId?: string,
    options?: Record<string, unknown>,
    formats?: IReportFormatInput,
    secondaryFilter?: ReportSecondaryFilterInput,
  ): Promise<string> {
    const result = await guardedQuery(runReportQuery, {
      report,
      effective,
      filter,
      groupBy,
      sort,
      viewId,
      options,
      formats,
      secondaryFilter,
    });
    return result?.data?.runReport;
  }

  static async getOccupationsByIds(
    returnSchema: string,
    occupationIds: string[],
  ): Promise<DeepPartial<IOccupation | undefined>[] | undefined> {
    const result = await guardedQuery(getOccupationsByIdsQuery(returnSchema), { ids: occupationIds });
    return result?.data?.occupationsByIds;
  }

  static async getMessagesByIds(
    returnSchema: string,
    messageIds: string[],
  ): Promise<DeepPartial<IMessage | undefined>[] | undefined> {
    const result = await guardedQuery(getMessagesByIdsQuery(returnSchema), { ids: messageIds });
    return result?.data?.messagesByIds;
  }

  static async getAuthenticationOptions(): Promise<IAuthenticationOptions | undefined> {
    const result = await guardedQuery(getAuthenticationOptionsQuery);
    return result?.data?.getAuthenticationOptions;
  }

  static async getReasons(
    returnSchema: string,
    filter?: ReasonFilter,
    sort?: ReasonSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IReason>>> {
    const result = await guardedQuery(getReasonsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.reasons;
  }

  static async getDocuments(
    type: string,
    returnSchema: string,
    filter?: ReasonFilter,
    sort?: ReasonSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IReason>>> {
    const result = await guardedQuery(getDocumentsQuery(returnSchema), { type, filter, sort, first, after });
    return result?.data?.documents;
  }

  static async getReasonsByIds(returnSchema: string, reasonIds: string[]): Promise<DeepPartial<IReason | undefined>[] | undefined> {
    const result = await guardedQuery(getReasonsByIdsQuery(returnSchema), { ids: reasonIds });
    return result?.data?.reasonsByIds;
  }

  static async getTimeOffTypes(
    returnSchema: string,
    filter?: TimeOffTypeFilter,
    sort?: TimeOffTypeSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimeOffType>>> {
    const result = await guardedQuery(getTimeOffTypesQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.timeOffTypes;
  }

  static async getEmployeeTimeOffTypes(employeeId: string, asof: Date): Promise<ITimeOffType[]> {
    const result = await guardedQuery(getEmployeeTimeOffTypesQuery, { employeeId, asof });
    return result?.data?.timeOffTypes?.data.map(TimeOffType.fromJson);
  }

  static async getTimeOffTypeTransactions(
    timeOffTypeId: string,
    employeeId: string,
    asof: Date,
  ): Promise<ITimeOffTypeTransaction[]> {
    const result = await guardedQuery(getTimeOffTypeTransactionsQuery, { timeOffTypeId, employeeId, asof });
    const transactions = result?.data?.timeOffTypesByIds?.[0].transactions;
    return transactions.length ? transactions[0].transactions : [];
  }

  static async getTimeOffTypesByIds(
    returnSchema: string,
    timeOffTypeIds: string[],
  ): Promise<DeepPartial<ITimeOffType | undefined>[] | undefined> {
    const result = await guardedQuery(getTimeOffTypesByIdsQuery(returnSchema), { ids: timeOffTypeIds });
    return result?.data?.timeOffTypesByIds;
  }

  static async getTemplates(
    returnSchema: string,
    filter?: TemplateFilter,
    sort?: TemplateSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITemplate>>> {
    const result = await guardedQuery(getTemplatesQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.templates;
  }

  static async getTemplatesByIds(
    returnSchema: string,
    templateIds: string[],
  ): Promise<DeepPartial<ITemplate | undefined>[] | undefined> {
    const result = await guardedQuery(getTemplatesByIdsQuery(returnSchema), { ids: templateIds });
    return result?.data?.templatesByIds;
  }

  static async getViews(
    returnSchema: string,
    filter?: ViewFilter,
    sort?: ViewSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IView>>> {
    const result = await guardedQuery(getViewsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.views;
  }

  static async getViewsByIds(returnSchema: string, viewIds: string[]): Promise<DeepPartial<IView | undefined>[] | undefined> {
    const result = await guardedQuery(getViewsByIdsQuery(returnSchema), { ids: viewIds });
    return result?.data?.viewsByIds;
  }

  static async getTimecardsByIds(
    returnSchema: string,
    timecardIds: string[],
  ): Promise<DeepPartial<ITimecard | undefined>[] | undefined> {
    const result = await guardedQuery(getTimecardsByIdsQuery(returnSchema), { ids: timecardIds });
    return result?.data?.timecardsByIds;
  }

  static async getBatchesSummary(filter: BatchFilterInput, first?: number, after?: string): Promise<IResult<IBatchTimecard>> {
    const result = await guardedQuery(getBatchesSummaryQuery, { filter, first, after });
    return result?.data?.batchesSummary;
  }

  static async getTimecards(
    returnSchema: string,
    effective: DateRange,
    filter?: TimecardFilterInput,
    sort?: TimecardSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimecard>>> {
    const result = await guardedQuery(getTimecardSearchQuery(returnSchema), { effective, filter, sort, first, after });
    const data = result?.data?.timecardSearch;
    return data;
  }

  static async getTimecardTests(
    returnSchema: string,
    filter?: TimecardTestFilterInput,
    sort?: any,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimecardTest>>> {
    // if (!sort) {
    //   sort = { employee: { direction: 'asc' } };
    // }
    const result = await guardedQuery(getTimecardTestsQuery(returnSchema), { filter, sort, first, after });
    return result?.data?.timecardTests;
  }

  static async getTimecardTestsByIds(returnSchema: string, ids: string[]): Promise<DeepPartial<ITimecardTest>[]> {
    const result = await guardedQuery(getTimecardTestsByIdsQuery(returnSchema), { ids });
    return result?.data?.timecardTestsByIds;
  }

  static async getTypeInfo(type: string): Promise<ITypeInfo> {
    const result = await guardedQuery(getTypeInfoQuery, { type });
    return result?.data?.__type;
  }

  static async getTags(documentType: UdfDocumentType, search?: string): Promise<string[]> {
    const result = await guardedQuery(getTagsQuery, { documentType, search });
    return result?.data?.tags;
  }

  static async getUdfs(
    returnSchema: string,
    documentType?: UdfDocumentType,
    filter?: UdfFilter,
    sort?: UdfSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IUdf>>> {
    const result = await guardedQuery(getUdfsQuery(returnSchema), { documentType, filter, sort, first, after });
    return result?.data?.udfs;
  }

  static async getUdfsByIds(returnSchema: string, udfIds: string[]): Promise<DeepPartial<IUdf | undefined>[] | undefined> {
    const result = await guardedQuery(getUdfsByIdsQuery(returnSchema), { ids: udfIds });
    return result?.data?.udfsByIds;
  }

  static async getBuiltInUdfs(
    returnSchema: string,
    documentType?: UdfDocumentType,
  ): Promise<DeepPartial<IUdf | undefined>[] | undefined> {
    const result = await guardedQuery(getBuiltInUdfsQuery(returnSchema), { documentType });
    return result?.data?.builtInUdfs;
  }

  static async getUdfDocumentTypes(): Promise<IUdfDocumentTypes | undefined> {
    const result = await guardedQuery(getUdfDocumentTypesQuery);
    return result?.data?.udfDocumentTypes;
  }

  static async getUdfsValuesSearchQuery(
    ids: string[],
    search?: string,
    returnSchema?: string,
  ): Promise<DeepPartial<INameEntity | undefined>[] | undefined> {
    const result = await guardedQuery(getUdfsValuesSearchQuery(returnSchema), { ids, search });
    const r = result?.data?.udfsByIds?.[0];
    if (!r) return [];
    return r.valuesSearch?.data;
  }

  static async getSearch(
    returnSchema: string,
    type: EntityType,
    search?: string,
    filter?: FieldFilter,
    count?: number,
  ): Promise<DeepPartial<ISearchResult>[]> {
    const result = await guardedQuery(getSearchQuery(returnSchema), { type, search, filter, count });
    return result?.data?.search;
  }

  static async getApplicationTemplatesSearch(search?: string): Promise<DeepPartial<ISearchResult>[]> {
    const result = await guardedQuery(getApplicationTemplatesSearchQuery, { search });
    return result?.data?.getApplicationTemplatesSearch;
  }

  static async getSchedulingToolEmployeeSchedule(
    returnSchema: string,
    effective: DateRange,
    ruleContext: IScheduleRuleContext,
    ruleQuery: string,
  ): Promise<IResult<DeepPartial<ISchedulingToolSchedule>>> {
    const result = await guardedQuery(getSchedulingToolEmployeeScheduleQuery(returnSchema), { effective, ruleContext, ruleQuery });
    return result?.data?.schedulingToolEmployeeSchedule;
  }

  static async getEmployeeSchedule(
    returnSchema: string,
    effective: DateRange,
    filter?: ScheduleFilterInput,
    groupBy?: string[],
    sort?: ScheduleSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ISchedule>>> {
    const result = await guardedQuery(getEmployeeScheduleQuery(returnSchema), { effective, filter, groupBy, sort, first, after });
    return result?.data?.employeeSchedule;
  }

  static async getTimeclocks(
    returnSchema: string,
    effective?: TimestampRange,
    filter?: TimeclocksFilter,
    sort?: TimeclocksSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimeclock>>> {
    const result = await guardedQuery(getTimeclocksQuery(returnSchema), { effective, filter, sort, first, after });
    return result?.data?.timeclocks;
  }

  static async getJobSchedule(
    returnSchema: string,
    effective: DateRange,
    filter?: ScheduleFilterInput,
    groupBy?: string[],
    sort?: ScheduleSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ISchedule>>> {
    const result = await guardedQuery(getJobScheduleQuery(returnSchema), { effective, filter, groupBy, sort, first, after });
    return result?.data?.jobSchedule;
  }

  static async scheduleSearch(
    returnSchema: string,
    search: string,
    effective?: DateRange,
    count?: number,
    from?: number,
  ): Promise<IScheduleSearchResult[]> {
    const result = await guardedQuery(scheduleSearchQuery(returnSchema), { search, effective, count, from });
    return result?.data?.scheduleSearch;
  }

  static async jobSearch(
    returnSchema: string,
    search: string,
    groupId?: string,
    employeeId?: string,
    effective?: UTC,
    count?: number,
    from?: number,
  ): Promise<IJobSearchResult[]> {
    const result = await guardedQuery(jobSearchQuery(returnSchema), { search, groupId, employeeId, effective, count, from });
    return result?.data?.jobSearch;
  }

  static async getGetJobSchedule(
    returnSchema: string,
    effective: DateRange,
    jobIds: string[],
  ): Promise<DeepPartial<ISavedSchedule>[]> {
    const result = await guardedQuery(getGetJobScheduleQuery(returnSchema), { effective, jobIds });
    return result?.data?.getJobSchedule;
  }

  static async getGetEmployeeSchedule(
    returnSchema: string,
    effective: DateRange,
    employeeIds: string[],
  ): Promise<DeepPartial<ISavedSchedule>[]> {
    const result = await guardedQuery(getGetEmployeeScheduleQuery(returnSchema), { effective, employeeIds });
    return result?.data?.getEmployeeSchedule;
  }

  static async getRegenerateJobSchedule(jobInfo: JobInfoInput[]): Promise<boolean> {
    const result = await guardedQuery(getRegenerateJobScheduleQuery, { jobInfo });
    return result?.data?.regenerateJobSchedule;
  }

  static async getBatches(filter?: any, first?: number, after?: string): Promise<IResult<DeepPartial<ISimplifiedBatch[]>>> {
    const result = await guardedQuery(getBatchesQuery, { filter, first, after });
    return result?.data?.batches;
  }

  static async getBatchesByIds(returnSchema: string, batchIds: string[]): Promise<DeepPartial<IBatch | undefined>[] | undefined> {
    const result = await guardedQuery(getBatchesByIdsQuery(returnSchema), { ids: batchIds });
    return result?.data?.batchesByIds;
  }

  static async getEmployeeTimecardSummaries(
    effective: DateRange,
    filter?: any,
    groupBy?: string,
    sort?: any,
    first?: number,
    after?: string,
  ): Promise<IBatchEmployeeTimecardResult> {
    if (!sort) {
      sort = { employee: { direction: 'asc' } };
    }
    const result = await guardedQuery(getEmployeeTimecardSummaryQuery, { effective, filter, groupBy, sort, first, after });
    return result?.data?.employeeTimecardSummary;
  }

  static async getTimecardWorkflows(): Promise<INameEntity[]> {
    const result = await guardedQuery(getTimecardWorkflowsQuery);
    return result?.data?.workflows;
  }

  static async getLocationWorkflow(): Promise<IWorkflow> {
    const result = await guardedQuery(getLocationWorkflowQuery, {});
    return result?.data?.locations?.data?.[0]?.workflow;
  }

  static async getPossibleTimecardStatuses(): Promise<string[]> {
    const result = await guardedQuery(getPossibleTimecardStatusesQuery);
    return result?.data?.possibleTimecardStatuses;
  }

  static async getPossibleTimecardTransitions(state: string): Promise<string[]> {
    const result = await guardedQuery(getPossibleTimecardTransitionsQuery, { state });
    return result?.data?.possibleTimecardTransitions;
  }

  static async getTimecardStatuses(
    employeeIds: string[],
    effective: DateRange,
    filter?: ITimecardStatusFilter,
  ): Promise<IEmployeeTimecardStatus[]> {
    const result = await guardedQuery(getTimecardStatusesQuery, { employeeIds, effective, filter });
    return result?.data?.getTimecardStatuses;
  }

  static async getSessionInfo(returnSchema: string): Promise<Partial<IUser>> {
    const result = await guardedQuery(getSessionInfoQuery(returnSchema));
    return result?.data?.getSessionInfo;
  }

  static async getSecurityTokens(returnSchema: string): Promise<DeepPartial<ISecurityToken[]>> {
    const result = await guardedQuery(getSecurityTokens(returnSchema));
    return result?.data?.securityTokens;
  }

  static async getDashboardGuestToken(id: string): Promise<IDashboardGuestToken> {
    const result = await guardedQuery(getDashboardGuestTokenQuery, { id });
    return result?.data?.getDashboardGuestToken;
  }

  static async getDashboards(): Promise<INameLike[]> {
    const result = await guardedQuery(getDashboardsQuery);
    return result?.data?.getDashboards;
  }

  static async getRuleSet(ruleSetId: string): Promise<INameEntity[]> {
    const result = await guardedQuery(getRuleSetQuery, { ruleSetId });
    return result?.data?.getRuleSet;
  }

  static async getRuleTable(tableId: string): Promise<IRuleSetTable> {
    const result = await guardedQuery(getRuleTable, { tableId });
    return result?.data?.getRuleTable;
  }

  static async getEmployeeSummaries(
    effective: DateRange,
    filter?: any,
    sort?: any,
    first?: number,
    after?: string,
  ): Promise<IResult<IEmployeeSummary>> {
    if (!sort) {
      sort = { employee: { direction: 'asc' } };
    }
    const result = await guardedQuery(getEmployeeSummariesQuery, { effective, filter, sort, first, after });
    return result?.data?.employeeList;
  }

  static async getMessagesSearch(
    returnSchema: string,
    filter?: MessageFilterInput,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IMessage>>> {
    const result = await guardedQuery(getMessagesQuery(returnSchema), { filter, first, after });
    return result?.data?.messagesSearch;
  }

  static async getUserDefinedTypes(returnSchema: string): Promise<IUdt[]> {
    const result = await guardedQuery(getUserDefinedTypesQuery(returnSchema));
    return result?.data?.udts;
  }

  static async getMessagesCounts(filter?: MessageFilterInput): Promise<IMessageLevelCount[]> {
    const result = await guardedQuery(getMessagesCountsQuery, { filter });
    return result?.data?.messagesCounts;
  }

  static subscribeToProcessedRules(employeeId: string, effective: DateRange, callback: any): any {
    return subscribe(rulesProcessedSubscription as GQL, { employeeId, effective }, callback, 'processedRules');
  }

  static async getTimeOffAccrualsByIds(ids: string[]): Promise<ITimeOffAccrual[] | undefined> {
    const result = await guardedQuery(getTimeOffAccrualsByIdsQuery, { ids });
    return result?.data?.timeOffAccrualsByIds;
  }

  static async getTimecardSummaryDashboard(effective: DateRange): Promise<ITimecardSummary> {
    const result = await guardedQuery(getTimecardSummaryDashboardQuery, { effective });
    return result?.data?.timecardSummaryDashboard;
  }

  static async getAdjustmentsTimecardSummaryDashboard(effective: DateRange): Promise<ITimecardSummary> {
    const result = await guardedQuery(getAdjustmentsTimecardSummaryDashboardQuery, { effective });
    return result?.data?.adjustmentsTimecardSummaryDashboard;
  }

  static async getTimecardsNotInBatch(
    effective: DateRange,
    areaIds: string[],
    groupIds: string[],
    startOfWeeks: string[],
  ): Promise<number> {
    const result = await guardedQuery(getTimecardsNotInBatchQuery, { effective, areaIds, groupIds, startOfWeeks });
    return result?.data?.timecardsNotInBatch;
  }

  static async getTimecardsNotInAdjustmentsBatch(
    effective: DateRange,
    areaIds: string[],
    groupIds: string[],
    startOfWeeks: string[],
  ): Promise<number> {
    const result = await guardedQuery(getTimecardsNotInAdjustmentsBatchQuery, { effective, areaIds, groupIds, startOfWeeks });
    return result?.data?.timecardsNotInAdjustmentsBatch;
  }

  static async loadValidationRules(): Promise<IValidationRuleSet | undefined> {
    const result = await guardedQuery(loadValidationRulesQuery);
    return result?.data?.loadValidationRules;
  }

  static async getTimeOffRequests(
    returnSchema: string,
    effective: DateRange,
    filter?: TimeOffRequestFilterInput,
    groupBy?: string[],
    sort?: TimeOffRequestSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimeOffRequest>>> {
    const result = await guardedQuery(getTimeOffRequestsQuery(returnSchema), { effective, filter, groupBy, sort, first, after });
    return result?.data?.timeOffRequests;
  }

  static async getTimeOffRequestsByIds(returnSchema: string, ids: string[]): Promise<DeepPartial<ITimeOffRequest>[] | undefined> {
    const result = await guardedQuery(getTimeOffRequestByIdsQuery(returnSchema), { ids });
    return result?.data?.timeOffRequestByIds;
  }

  static async getTimeOffRequestsForTimeOffBalances(
    returnSchema: string,
    effective: DateRange,
    filter?: TimeOffRequestFilterInput,
    groupBy?: string[],
    sort?: TimeOffRequestSort,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<ITimeOffRequest>>> {
    const result = await guardedQuery(getTimeOffRequestsForTimeOffBalancesQuery(returnSchema), {
      effective,
      filter,
      groupBy,
      sort,
      first,
      after,
    });
    return result?.data?.timeOffRequestsForTimeOffBalances;
  }

  static async getTimeOffRequestStatus(timeOffRequestId: string): Promise<ITimeOffRequestStatus> {
    const result = await guardedQuery(getTimeOffRequestStatusQuery, { timeOffRequestId });
    return result?.data?.getTimeOffRequestStatus || [];
  }

  static async getPossibleTimeOffRequestStatuses(): Promise<string[]> {
    const result = await guardedQuery(getPossibleTimeOffRequestStatusesQuery);
    return result?.data?.possibleTimeOffRequestStatuses || [];
  }

  static async getTimeOffRequestWorkflows(): Promise<INameEntity[]> {
    const result = await guardedQuery(getTimeOffRequestsWorkflowsQuery);
    return result?.data?.timeOffRequestWorkflows;
  }

  static async hasEmployeeMobileAccess(email: string): Promise<boolean> {
    const result = await query(hasEmployeeMobileAccessQuery, { email: email });
    return result?.data?.hasEmployeeMobileAccess;
  }

  static async getAuditLogs(
    returnSchema: string,
    filter?: AuditLogFilterInput,
    entityDataFilters?: FieldFilter[],
    sort?: AuditLogSortInput,
    first?: number,
    after?: string,
  ): Promise<IResult<DeepPartial<IAuditLog>>> {
    const result = await guardedQuery(getAuditLogsQuery(returnSchema), { filter, entityDataFilters, sort, first, after });
    return result?.data?.logs;
  }

  static async getScheduleRuleDefaults(ruleContext: IScheduleRuleContext): Promise<INameLike[]> {
    const result = await guardedQuery(getScheduleRuleDefaultsQuery, { context: ruleContext });
    return result?.data?.getScheduleRuleDefaults;
  }
}
